import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';
import { authService } from './auth.service';

// GraphQL query for fetching paginated users
export const GET_USERS_BY_ROLE_QUERY = gql`
  query GetUsersByRole(
    $where: UserWhereInput
    $pagination: UserPaginationArgs
  ) {
    usersPaginated(where: $where, pagination: $pagination) {
      users {
        id
        username
        name
        email
        phone
        role {
          id
          name
        }
        Balance {
          id
          amount
          credit
          onHold
        }
        credit
        balanceIn
        balanceOut
        isActive
        parentId
        comment
        lastLogin
        loginIp
        avatarUrl
        language
        timezone
        createdAt
        updatedAt
        isCashbackEnabled
        isWagerEnabled
        isOnlinePaymentEnabled
      }
      totalBalance
      totalBalanceIn
      totalBalanceOut
      totalProfit
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
        total
      }
    }
  }
`;

// Types for the response
export interface Balance {
  id: number;
  amount: number;
  credit: number;
  onHold: number;
}

export interface Role {
  id: number;
  name: string;
}

export interface User {
  id: number;
  username: string;
  email?: string;
  phone?: string;
  role: Role;
  Balance?: Balance;
  credit: number;
  isActive: boolean;
  parentId?: number;
  comment?: string;
  lastLogin?: string;
  balanceOut: number;
  balanceIn: number;
  loginIp?: string;
  avatarUrl?: string;
  language?: string;
  timezone?: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  isWagerEnabled: boolean;
  isCashbackEnabled: boolean;
  isOnlinePaymentEnabled: boolean;
}

export interface PageInfo {
  startCursor?: string;
  endCursor?: string;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  total: number;
}

export interface UsersPaginatedResponse {
  users: User[];
  totalBalance: number;
  totalBalanceIn: number;
  totalBalanceOut: number;
  totalProfit: number;
  pageInfo: PageInfo;
}

export interface GetUsersByRoleResponse {
  usersPaginated: UsersPaginatedResponse;
}

// Role-based users service class
export class RoleBasedUsersService {
  private static instance: RoleBasedUsersService;

  private constructor() { }

  public static getInstance(): RoleBasedUsersService {
    if (!RoleBasedUsersService.instance) {
      RoleBasedUsersService.instance = new RoleBasedUsersService();
    }
    return RoleBasedUsersService.instance;
  }

  // Fetch users by role name (dynamic role ID)
  async getUsersByRole(
    roleName: string,
    first: number = 10,
    after?: string | number,
    parentId?: number,
  ): Promise<UsersPaginatedResponse> {
    try {
      const roleId = authService.getRoleIdByName(roleName);

      if (!roleId) {
        throw new Error(
          `Role "${roleName}" not found. Please ensure roles are loaded.`,
        );
      }

      const response = await apolloClient.query<GetUsersByRoleResponse>({
        query: GET_USERS_BY_ROLE_QUERY,
        variables: {
          where: { roleId, parentId },
          pagination: { first, after },
        },
        fetchPolicy: 'network-only',
      });
      if (response.errors && response.errors.length > 0) {
        throw response;
      }

      if (!response.data || !response.data.usersPaginated) {
        throw new Error('No data returned from getUsersByRole query');
      }

      return response.data.usersPaginated;
    } catch (error: any) {
      console.error('Error fetching users by role:', error);

      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }

      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }

      throw error;
    }
  }

  // Fetch users with custom filters using role name
  async getUsersWithFiltersByRole(
    roleName: string,
    additionalFilters: any = {},
    pagination: any = {},
  ): Promise<UsersPaginatedResponse> {
    try {
      const roleId = authService.getRoleIdByName(roleName);

      if (!roleId) {
        throw new Error(
          `Role "${roleName}" not found. Please ensure roles are loaded.`,
        );
      }

      const where = {
        roleId,
        ...additionalFilters,
      };

      const response = await apolloClient.query<GetUsersByRoleResponse>({
        query: GET_USERS_BY_ROLE_QUERY,
        variables: {
          where,
          pagination,
        },
        fetchPolicy: 'network-only',
      });

      if (response.errors && response.errors.length > 0) {
        throw response;
      }

      if (!response.data || !response.data.usersPaginated) {
        throw new Error(
          'No data returned from getUsersWithFiltersByRole query',
        );
      }

      return response.data.usersPaginated;
    } catch (error: any) {
      console.error('Error fetching users with filters by role:', error);
      throw error;
    }
  }

  // Get available roles for the current user
  getAvailableRoles(): string[] {
    const roles = authService.getRoles();
    return roles.map((role) => role.name);
  }

  // Check if a role exists
  roleExists(roleName: string): boolean {
    return authService.getRoleIdByName(roleName) !== null;
  }
}

// Export singleton instance
export const roleBasedUsersService = RoleBasedUsersService.getInstance();

// Export convenience functions
export async function getUsersByRole(
  roleName: string,
  first: number = 10,
  after?: string,
): Promise<UsersPaginatedResponse> {
  return roleBasedUsersService.getUsersByRole(roleName, first, after);
}
export async function getUsersByRoleParentId(
  parentId: number,
  roleName: string,
  first: number = 10,
  after?: number,
): Promise<UsersPaginatedResponse> {
  return roleBasedUsersService.getUsersByRole(roleName, first, after, parentId);
}

export async function getUsersWithFiltersByRole(
  roleName: string,
  additionalFilters: any = {},
  pagination: any = {},
): Promise<UsersPaginatedResponse> {
  return roleBasedUsersService.getUsersWithFiltersByRole(
    roleName,
    additionalFilters,
    pagination,
  );
}
